/**
 * ChatGPT Ads Measurement Pixel.
 * Loads https://bzrcdn.openai.com/sdk/oaiq.min.js only after marketing
 * consent, captures the landing-page click id (oppref), and exposes
 * trackChatGptOrder for the shop confirmation page.
 * debug stays off. The server Conversions API is not called from the browser.
 */
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const PIXEL_ID = "AUyrLnUP3tyYwAmYhYa9ea";
const SDK_SRC = "https://bzrcdn.openai.com/sdk/oaiq.min.js";
const CONSENT_STORAGE_KEY = "cookie_consent_v1";
const OPPREF_KEY = "du_oppref";
const DEDUP_PREFIX = "du_oaiq_order_";

let pixelInitialized = false;

function hasMarketingConsent() {
  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return false;
    const saved = JSON.parse(raw);
    return !!(saved?.choices?.marketing);
  } catch {
    return false;
  }
}

function rememberOppref() {
  try {
    const value = new URLSearchParams(window.location.search).get("oppref");
    if (value) sessionStorage.setItem(OPPREF_KEY, value);
  } catch {
    /* private mode */
  }
}

/** Put a stored click id back on the URL so the SDK can write its cookie. */
function restoreOpprefForSdk() {
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get("oppref")) return;
    const stored = sessionStorage.getItem(OPPREF_KEY);
    if (!stored) return;
    const url = new URL(window.location.href);
    url.searchParams.set("oppref", stored);
    window.history.replaceState(window.history.state, "", url);
  } catch {
    /* ignore */
  }
}

function installQueue() {
  if (window.oaiq) return;
  const q = function oaiqQueue() {
    q.q.push(arguments);
  };
  q.q = [];
  window.oaiq = q;
  const script = document.createElement("script");
  script.async = true;
  script.src = SDK_SRC;
  const first = document.getElementsByTagName("script")[0];
  if (first && first.parentNode) first.parentNode.insertBefore(script, first);
  else document.head.appendChild(script);
}

function ensurePixel() {
  if (!hasMarketingConsent()) {
    if (typeof window.oaiq === "function") window.oaiq("consent", false);
    return false;
  }
  restoreOpprefForSdk();
  installQueue();
  window.oaiq("consent", true);
  if (!pixelInitialized) {
    window.oaiq("init", { pixelId: PIXEL_ID });
    pixelInitialized = true;
  }
  return true;
}

/**
 * Bestellung auf /shop/success. amount ist der Betrag in Cent.
 * transactionId wird die Ereignis-ID, damit ein späterer Server-Aufruf
 * dieselbe Bestellung nicht doppelt zählt.
 */
export function trackChatGptOrder({ value, transactionId, currency = "EUR", quantity = 1, contentName }) {
  try {
    if (!ensurePixel()) return;
    const id = String(transactionId || "").trim();
    if (id) {
      try {
        if (localStorage.getItem(`${DEDUP_PREFIX}${id}`)) return;
        localStorage.setItem(`${DEDUP_PREFIX}${id}`, "1");
      } catch {
        /* private mode */
      }
    }
    const euros = Number(value);
    const amount = Number.isFinite(euros) ? Math.round(euros * 100) : 0;
    const qty = Math.max(1, parseInt(quantity, 10) || 1);
    const data = { type: "contents", amount, currency };
    if (id) {
      const item = { id, content_type: "product", quantity: qty };
      if (contentName) item.name = String(contentName);
      data.contents = [item];
    }
    if (id) window.oaiq("measure", "order_created", data, { event_id: id });
    else window.oaiq("measure", "order_created", data);
  } catch (e) {
    // Tracking must not affect checkout.
    // eslint-disable-next-line no-console
    console.warn("ChatGPT Ads conversion tracking failed:", e);
  }
}

const ChatGptAdsPixel = () => {
  const location = useLocation();

  useEffect(() => {
    rememberOppref();
    ensurePixel();
    const onConsentChanged = () => ensurePixel();
    window.addEventListener("cookie-consent-changed", onConsentChanged);
    return () => window.removeEventListener("cookie-consent-changed", onConsentChanged);
  }, [location.pathname, location.search]);

  return null;
};

export default ChatGptAdsPixel;
